import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { buildAppMeta } from "@/lib/seo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  type Clinic,
  type ClinicRegistration,
  REGISTRATION_STATUS_LABELS,
  PAYMENT_MODE_LABELS,
  fmtClinicPrice,
  publicClinicUrl,
} from "@/lib/clinics";
import {
  removeClinicAttendee,
  cancelClinicAndNotify,
  emailClinicAttendees,
} from "@/lib/clinics.functions";
import { fmtBookingDate, fmtSlotTime } from "@/lib/booking";
import { ArrowLeft, CalendarX2, Copy, ExternalLink, Loader2, Mail, Users, X } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/app/clinics_/$clinicId")({
  head: () => ({
    meta: buildAppMeta({
      title: "Clinic roster — CoachQuick",
      description: "Manage a clinic's roster and waitlist.",
      path: "/app/clinics",
    }),
  }),
  component: () => (
    <RequireAuth role="coach">
      <ClinicDetailPage />
    </RequireAuth>
  ),
});

function ClinicDetailPage() {
  const { clinicId } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const removeAttendee = useServerFn(removeClinicAttendee);
  const cancelClinic = useServerFn(cancelClinicAndNotify);
  const emailAttendees = useServerFn(emailClinicAttendees);
  const [composerOpen, setComposerOpen] = useState(false);

  const clinicQ = useQuery({
    queryKey: ["clinic-detail", clinicId],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clinics")
        .select("*")
        .eq("id", clinicId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as Clinic | null) ?? null;
    },
  });

  const registrationsQ = useQuery({
    queryKey: ["clinic-registrations", clinicId],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clinic_registrations")
        .select("*")
        .eq("clinic_id", clinicId)
        .order("created_at");
      if (error) throw error;
      return (data as unknown as ClinicRegistration[]) ?? [];
    },
  });

  const clinic = clinicQ.data;
  const regs = useMemo(() => registrationsQ.data ?? [], [registrationsQ.data]);
  const active = regs.filter((r) => r.status === "registered" || r.status === "pending_payment");
  const waitlisted = regs.filter((r) => r.status === "waitlisted");
  const inactive = regs.filter(
    (r) => r.status === "cancelled" || r.status === "removed" || r.status === "expired",
  );

  async function copyLink() {
    if (!clinic) return;
    await navigator.clipboard.writeText(publicClinicUrl(clinic.public_token));
    toast.success("Signup link copied");
  }

  const removeMut = useMutation({
    mutationFn: async (registrationId: string) => {
      const res = await removeAttendee({ data: { registrationId } });
      if (!res.ok) throw new Error(res.error ?? "failed");
    },
    onSuccess: () => {
      toast.success("Attendee removed — they've been emailed.");
      qc.invalidateQueries({ queryKey: ["clinic-registrations", clinicId] });
    },
    onError: () => toast.error("Couldn't remove that attendee — try again."),
  });

  const cancelMut = useMutation({
    mutationFn: async () => {
      const res = await cancelClinic({ data: { clinicId } });
      if (!res.ok) throw new Error("failed");
      return res;
    },
    onSuccess: (res) => {
      toast.success(
        res.notified > 0
          ? `Clinic cancelled — ${res.notified} ${res.notified === 1 ? "attendee" : "attendees"} emailed.`
          : "Clinic cancelled.",
      );
      qc.invalidateQueries({ queryKey: ["clinic-detail", clinicId] });
      qc.invalidateQueries({ queryKey: ["clinic-registrations", clinicId] });
      qc.invalidateQueries({ queryKey: ["coach-clinics", user?.id] });
    },
    onError: () => toast.error("Couldn't cancel the clinic — try again."),
  });

  if (clinicQ.isLoading) {
    return (
      <AppShell>
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </AppShell>
    );
  }

  if (!clinic) {
    return (
      <AppShell>
        <p className="mt-8 text-sm text-muted-foreground">Clinic not found.</p>
        <Button variant="outline" size="sm" className="mt-3" asChild>
          <Link to="/app/clinics">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to clinics
          </Link>
        </Button>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-5">
        <Link
          to="/app/clinics"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Clinics
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tight text-foreground">
              {clinic.title}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {fmtBookingDate(clinic.clinic_date)} · {fmtSlotTime(clinic.start_time)}–
              {fmtSlotTime(clinic.end_time)} ·{" "}
              {clinic.price_cents > 0
                ? fmtClinicPrice(clinic.price_cents, clinic.currency)
                : "Free"}{" "}
              · {PAYMENT_MODE_LABELS[clinic.payment_mode]}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {(active.length > 0 || waitlisted.length > 0) && clinic.status !== "cancelled" && (
              <Button size="sm" onClick={() => setComposerOpen(true)}>
                <Mail className="h-3.5 w-3.5 mr-1" /> Message attendees
              </Button>
            )}
            {clinic.status === "published" && (
              <>
                <Button size="sm" variant="outline" onClick={copyLink}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href={publicClinicUrl(clinic.public_token)} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </>
            )}
            {clinic.status !== "cancelled" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-destructive">
                    <CalendarX2 className="h-3.5 w-3.5 mr-1" /> Cancel clinic
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel this clinic?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Everyone on the roster and waitlist will be emailed that the clinic is
                      cancelled. The signup page stops taking registrations. This can't be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep the clinic</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => cancelMut.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Cancel clinic
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      <Card className="p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Users className="h-4 w-4 text-brand-text" /> Roster ({active.length}/{clinic.capacity})
        </h2>
        {active.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">No one has signed up yet.</p>
        ) : (
          <RosterTable
            regs={active}
            onRemove={clinic.status !== "cancelled" ? (id) => removeMut.mutate(id) : undefined}
          />
        )}
      </Card>

      {waitlisted.length > 0 && (
        <Card className="mt-4 p-5">
          <h2 className="text-sm font-semibold">Waitlist ({waitlisted.length})</h2>
          <RosterTable
            regs={waitlisted}
            onRemove={clinic.status !== "cancelled" ? (id) => removeMut.mutate(id) : undefined}
          />
        </Card>
      )}

      {inactive.length > 0 && (
        <Card className="mt-4 p-5">
          <h2 className="text-sm font-semibold text-muted-foreground">No longer attending</h2>
          <RosterTable regs={inactive} />
        </Card>
      )}

      <MessageComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        rosterCount={active.length}
        waitlistCount={waitlisted.length}
        onSend={async (input) => {
          const res = await emailAttendees({ data: { clinicId, ...input } });
          if (res.ok) {
            toast.success(
              `Message queued for ${res.sent} ${res.sent === 1 ? "attendee" : "attendees"}.`,
            );
            setComposerOpen(false);
          } else {
            toast.error("Couldn't send — try again.");
          }
        }}
      />
    </AppShell>
  );
}

function MessageComposer(props: {
  open: boolean;
  rosterCount: number;
  waitlistCount: number;
  onClose: () => void;
  onSend: (input: { subject: string; message: string; includeWaitlist: boolean }) => Promise<void>;
}) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeWaitlist, setIncludeWaitlist] = useState(false);
  const [sending, setSending] = useState(false);

  const recipients = props.rosterCount + (includeWaitlist ? props.waitlistCount : 0);

  async function send() {
    setSending(true);
    try {
      await props.onSend({ subject: subject.trim(), message: message.trim(), includeWaitlist });
      setSubject("");
      setMessage("");
      setIncludeWaitlist(false);
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Message attendees</DialogTitle>
          <DialogDescription>
            Sent as individual emails from CoachQuick — recipients never see each other's addresses.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="msg-subject" className="text-xs">
              Subject
            </Label>
            <Input
              id="msg-subject"
              value={subject}
              maxLength={150}
              placeholder="Bring water — it's going to be hot!"
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="msg-body" className="text-xs">
              Message
            </Label>
            <Textarea
              id="msg-body"
              value={message}
              rows={6}
              maxLength={5000}
              placeholder="What should attendees know?"
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          {props.waitlistCount > 0 && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={includeWaitlist}
                onCheckedChange={(v) => setIncludeWaitlist(v === true)}
              />
              Also send to the waitlist ({props.waitlistCount})
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            disabled={sending || !subject.trim() || !message.trim() || recipients === 0}
            onClick={send}
          >
            {sending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Send to {recipients} {recipients === 1 ? "person" : "people"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RosterTable({
  regs,
  onRemove,
}: {
  regs: ClinicRegistration[];
  onRemove?: (registrationId: string) => void;
}) {
  return (
    <div className="mt-3 divide-y">
      {regs.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{r.attendee_name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {r.attendee_email}
              {r.attendee_phone ? ` · ${r.attendee_phone}` : ""}
            </p>
            {r.note && <p className="mt-0.5 text-xs text-muted-foreground italic">“{r.note}”</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Badge variant="secondary">{REGISTRATION_STATUS_LABELS[r.status]}</Badge>
            {onRemove && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {r.attendee_name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      They'll be emailed that they were removed. If the clinic was full, the first
                      waitlisted person is promoted automatically.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep them</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onRemove(r.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
